import { useCallback, useEffect, useRef, useState } from 'react';
import { Content, Header, HeaderLabel, Page } from '@backstage/core-components';
import { loadStylesheets, sanitizeHtml } from '../../utils/misc';
import { makeStyles } from '@mui/styles';
import { useTheme } from '@mui/material/styles';
import { useApi } from '@backstage/core-plugin-api';
import { useAcademyEntity } from '../../hooks/useAcademyEntity';
import LinearProgress from '@mui/material/LinearProgress';
import { CodelabOverlays } from './CodelabOverlays';
import { CustomSnackbar } from '../CustomSnackbar/CustomSnackbar';
import { useTutorialProgress } from '../../hooks/useTutorialProgress';
import { userSettingsApiRef } from '@mercedes-benz/user-settings-react';
import { ErrorPage } from './ErrorPage/ErrorPage';
import Container from '@mui/material/Container';
import {
  FEATURE_ACADEMY_PROGRESS_MAP,
  USER_PREFERENCES,
  UserSettingsApi,
} from '@mercedes-benz/user-settings-common';
import {
  TutorialProgressEntry,
  TutorialProgressMap,
} from '@mercedes-benz/academy-common';
import { MAX_PROGRESS_ENTRIES, PROGRESS_MESSAGES } from '../../constants';

async function updateTutorialProgress(
  userSettingsApi: UserSettingsApi,
  name: string,
  patch: Partial<TutorialProgressEntry>,
) {
  const setting = await userSettingsApi.get({
    bucket: USER_PREFERENCES,
    key: FEATURE_ACADEMY_PROGRESS_MAP,
  });

  const stored: TutorialProgressMap =
    (setting?.value as unknown as TutorialProgressMap) ?? {};

  const existing = stored[name] ?? {
    progress: '#0',
    totalSteps: 0,
    updatedAt: 0,
  };
  let updated: TutorialProgressMap = {
    ...stored,
    [name]: { ...existing, ...patch, updatedAt: Date.now() },
  };

  // Evict oldest entries when over cap
  const entries = Object.entries(updated);
  if (entries.length > MAX_PROGRESS_ENTRIES) {
    entries.sort((a, b) => a[1].updatedAt - b[1].updatedAt);
    updated = Object.fromEntries(
      entries.slice(entries.length - MAX_PROGRESS_ENTRIES),
    );
  }

  await userSettingsApi.set({
    bucket: USER_PREFERENCES,
    key: FEATURE_ACADEMY_PROGRESS_MAP,
    value: updated as unknown as any,
  });
}

const stylesheetUrls = [
  '/academy/material-icons.css?v=1.0.0',
  '/academy/codelab-elements.css?v=1.0.0',
  'https://fonts.googleapis.com/css2?family=Material+Icons', // Added this as loading font-face from /academy/material-icons.woff2 is slow
];

const scripts = ['codelab-elements.js', 'native-shim.js', 'prettify.js'];

const lightModeStyles = {
  border: '1px solid rgba(255, 255, 255, 0.5)',
  borderRadius: '0px',
  background: 'transparent',
  color: 'rgb(26, 26, 26)',
  baseBackgroundColor: 'rgb(255, 255, 255)',
  backgroundColor: 'rgb(255, 255, 255)',
  backgroundAlt: 'rgb(198,198,198)',
  colorAlt: 'rgb(26, 26, 26)',
};

const darkModeStyles = {
  border: '1px solid rgba(255, 255, 255, 0.5)',
  borderRadius: '0px',
  background: 'transparent',
  color: 'rgb(255, 255, 255)',
  baseBackgroundColor: 'rgb(0, 0, 0)',
  backgroundColor: 'rgb(26, 26, 26)',
  backgroundAlt: 'rgb(59, 59, 59)',
  colorAlt: 'rgb(255, 255, 255)',
};

const useStyles = makeStyles(theme => {
  const modeStyles =
    theme.palette.mode === 'light' ? lightModeStyles : darkModeStyles;
  const isLight = theme.palette.mode === 'light';
  const fgAlpha = (a: number) =>
    isLight ? `rgba(0, 0, 0, ${a})` : `rgba(255, 255, 255, ${a})`;
  const borderColor = isLight
    ? 'rgba(0, 0, 0, 0.1)'
    : 'rgba(255, 255, 255, 0.1)';
  const primaryMain = '#0078D6';

  return {
    layout: {
      display: 'flex',
      height: '100%',
      overflow: 'hidden',
    },
    externalWrapper: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      padding: '0 24px 24px',
    },
    contentArea: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      minWidth: 0,
    },
    codelabScroll: {
      flex: 1,
      overflowY: 'auto',
      padding: '0 24px 24px',
    },
    sidebar: {
      width: 220,
      minWidth: 220,
      flexShrink: 0,
      borderLeft: `1px solid ${borderColor}`,
      padding: '16px 16px',
      overflowY: 'auto',
      background: 'transparent',
    },
    sidebarHeader: {
      fontFamily: "'MB Corpo S Text', 'Helvetica Neue', Arial, sans-serif",
      fontSize: '0.75rem',
      fontWeight: 600,
      color: fgAlpha(0.5),
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em',
      marginBottom: 10,
      userSelect: 'none' as const,
    },
    navList: {
      listStyle: 'none',
      margin: 0,
      padding: 0,
    },
    navLink: {
      display: 'block',
      padding: '4px 0',
      fontFamily: "'MB Corpo S Text', 'Helvetica Neue', Arial, sans-serif",
      fontSize: '0.78rem',
      color: fgAlpha(0.65),
      textDecoration: 'none',
      lineHeight: 1.4,
      cursor: 'pointer',
      border: 'none',
      background: 'transparent',
      width: '100%',
      textAlign: 'left' as const,
      transition: 'color 0.12s ease',
      '&:hover': {
        color: primaryMain,
      },
    },
    navLinkActive: {
      color: primaryMain,
      fontWeight: 600,
      background: isLight
        ? 'rgba(0, 120, 214, 0.08)'
        : 'rgba(0, 120, 214, 0.15)',
      borderLeft: `2px solid ${primaryMain}`,
      borderRadius: '0 4px 4px 0',
      paddingLeft: 8,
      marginLeft: -10,
    },
    footer: {
      flexShrink: 0,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 24px',
      borderTop: `1px solid ${borderColor}`,
      background: 'transparent',
      gap: 8,
    },
    footerBtn: {
      background: 'none',
      border: `1px solid ${borderColor}`,
      borderRadius: 6,
      padding: '6px 14px',
      color: fgAlpha(0.65),
      cursor: 'pointer',
      fontFamily: "'MB Corpo S Text', 'Helvetica Neue', Arial, sans-serif",
      fontSize: '0.8rem',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      transition: 'border-color 0.15s ease, color 0.15s ease',
      '&:hover': {
        borderColor: primaryMain,
        color: isLight ? 'rgba(0, 0, 0, 0.85)' : '#ffffff',
      },
      '&:disabled': {
        opacity: 0.35,
        cursor: 'default',
        '&:hover': {
          borderColor,
          color: fgAlpha(0.65),
        },
      },
    },
    footerBtnPlaceholder: {
      visibility: 'hidden' as const,
      width: 80,
    },
    codelabs: {
      flex: 1,
      minHeight: 0,
      position: 'relative' as const,
      color: modeStyles.color,
      '& #codelab-title': {
        position: 'absolute',
        background: 'transparent',
        color: modeStyles.color,
        boxShadow: 'none',
      },
      '& #time-remaining': {
        display: 'none',
      },
      '& google-codelab': {
        height: '100%',
        display: 'block',
        '& #drawer': {
          display: 'none !important',
        },
        '& #controls': {
          display: 'none !important',
        },
        '& .steps': {
          backgroundColor: modeStyles.baseBackgroundColor,
        },
        '& .metadata': {
          backgroundColor: modeStyles.baseBackgroundColor,
        },
        '& #codelab-nav-buttons #arrow-back': {
          display: 'none !important' as any,
        },
        '& #main': {
          background: 'transparent',
          marginLeft: '0 !important',
          height: '100%',
          position: 'relative !important' as any,
        },
        '& #steps': {
          maxHeight: 'none',
          height: '100%',
          overflowY: 'auto',
          background: 'transparent',
          paddingBottom: '180px',
        },
      },
      '& google-codelab-step': {
        background: 'transparent !important',
        color: `${modeStyles.color} !important`,
      },
      '& google-codelab-step .instructions': {
        maxWidth: '1200px !important',
        margin: '0 auto 24px !important',
        borderRadius: '4px !important',
        background: 'transparent !important',
        color: `${modeStyles.color} !important`,
        boxShadow: 'none !important',
        '& :not(pre) > code': {
          background: modeStyles.backgroundAlt,
        },
        '& h2, & h3, & h4': {
          color: `${modeStyles.color} !important`,
        },
      },
    },
    codelabsLegacy: {
      position: 'relative',
      height: '100%',
      '& #previous-step': {
        border: modeStyles.border,
        borderRadius: modeStyles.borderRadius,
        background: modeStyles.background,
        color: modeStyles.color,
      },
      '& #next-step': {
        borderRadius: modeStyles.borderRadius,
      },
      '& #done': {
        borderRadius: modeStyles.borderRadius,
      },
      '& #codelab-title': {
        position: 'absolute',
        background: modeStyles.backgroundColor,
        color: modeStyles.color,
      },
      '& google-codelab': {
        '& .steps': {
          backgroundColor: modeStyles.baseBackgroundColor,
        },
        '& .metadata': {
          backgroundColor: modeStyles.baseBackgroundColor,
        },
        '& #codelab-nav-buttons #arrow-back': {
          color: modeStyles.color,
        },
        '& #main': {
          background: modeStyles.baseBackgroundColor,
        },
        '& #steps': {
          maxHeight: '90%',
          background: modeStyles.backgroundColor,
        },
        '& #controls': {
          background: modeStyles.baseBackgroundColor,
          paddingTop: '16px',
        },
        '& #drawer ol': {
          '& li[selected] a:focus': {
            background: modeStyles.backgroundAlt,
          },
          '& li[completed] a': {
            color: modeStyles.colorAlt,
            background: modeStyles.baseBackgroundColor,
          },
          '& li a': {
            background: modeStyles.backgroundColor,
            color: modeStyles.color,
          },
        },
      },
      '& google-codelab-step': {
        background: `${modeStyles.baseBackgroundColor} !important`,
      },
      '& google-codelab-step .instructions': {
        maxWidth: '1200px !important',
        margin: '0 auto 90px !important',
        borderRadius: '4px !important',
        background: `${modeStyles.backgroundColor} !important`,
        '& :not(pre) > code': {
          background: modeStyles.backgroundAlt,
        },
      },
    },
  };
});

function appenScripts(
  url: string,
  resolve: (value: PromiseLike<void> | void) => void,
  reject: (reason?: any) => void,
  setError: (err: string) => void,
) {
  const versionedUrl = `${url}?v=1.0.0`;

  if (document.querySelector(`script[src="${versionedUrl}"]`)) {
    resolve();
    return;
  }

  const script = document.createElement('script');
  script.src = versionedUrl;
  script.async = true;

  script.onload = () => {
    resolve();
  };

  script.onerror = err => {
    let errorStr: string;
    if (typeof err === 'string') {
      errorStr = `Error loading script ${url}: ${err}`;
    } else {
      errorStr = `An error occurred while loading the script: ${url}`;
    }
    // eslint-disable-next-line no-console
    console.error(errorStr);
    setError(errorStr);
    reject(new Error(errorStr));
  };

  document.head.appendChild(script);
}

function loadScripts(
  scriptUrls: string[],
  setError: (err: string) => void,
): Promise<void[]> {
  return Promise.all(
    scriptUrls.map(
      url =>
        new Promise<void>((resolve, reject) => {
          appenScripts(url, resolve, reject, setError);
        }),
    ),
  );
}

function addDataTestIds(htmlFile: Document): Document {
  const previousStep = htmlFile.querySelector('a#previous-step');
  if (previousStep) {
    previousStep.setAttribute('data-testid', 'previous-step-button');
  }

  const nextStepLink = htmlFile.querySelector('a#next-step');
  if (nextStepLink) {
    nextStepLink.setAttribute('data-testid', 'next-step-button');
  }

  return htmlFile;
}

async function renderCodelab(url: string) {
  const response = await fetch(url);
  let htmlString = await response.text();

  // Parse the HTML string to a DOM object
  const parser = new DOMParser();
  const htmlFile = parser.parseFromString(htmlString, 'text/html');

  const htmlFileWithTestIds = addDataTestIds(htmlFile);

  const serializer = new XMLSerializer();
  htmlString = serializer.serializeToString(htmlFileWithTestIds);

  const academyEntity = htmlString.match(
    /<google-codelab codelab-gaid=".*?">(.*?)<\/google-codelab>/gs,
  );
  if (!academyEntity || academyEntity.length === 0) {
    throw new Error('No codelab found');
  }

  return sanitizeHtml(academyEntity[0]);
}

export type CodelabNavState = {
  steps: string[];
  currentStep: number;
  onStepClick: (index: number) => void;
  onPrev: () => void;
  onNext: () => void;
  onDone?: () => void;
};

type CodelabProps = {
  namespaceId: string;
  tutorialName: string;
  showHeader?: boolean;
  onNavReady?: (nav: CodelabNavState) => void;
  onDone?: () => void;
  externalNav?: boolean;
};

export function Codelab({
  namespaceId,
  tutorialName,
  showHeader = true,
  onNavReady,
  onDone,
  externalNav = false,
}: Readonly<CodelabProps>) {
  const [loadingAcademy, setLoadingAcademy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [codelabHtml, setCodelabHtml] = useState<undefined | string>(undefined);
  const [steps, setSteps] = useState<string[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const classes = useStyles(theme);
  const {
    error: academyError,
    loading,
    entity,
    imgUrl,
    indexUrl,
    academyBaseUrl,
  } = useAcademyEntity(namespaceId, tutorialName);

  const { progress, isNotificationOpen, setIsNotificationOpen } =
    useTutorialProgress(entity);
  const userSettingsApi = useApi(userSettingsApiRef);

  // Clear text selection when component unmounts (e.g., navigating away)
  useEffect(() => {
    return () => {
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
      }
    };
  }, []);

  // Parse step labels and track current step once the codelab HTML is rendered
  useEffect(() => {
    if (!codelabHtml) return () => {};

    const parseSteps = () => {
      const stepEls = document.querySelectorAll('google-codelab-step');
      if (stepEls.length > 0) {
        const total = stepEls.length;
        setSteps(
          Array.from(stepEls).map(
            (el, i) => el.getAttribute('label') ?? `Step ${i + 1}`,
          ),
        );
        const hash = globalThis.location.hash.replace('#', '');
        const idx = parseInt(hash, 10);
        const step = Number.isFinite(idx) ? idx : 0;
        setCurrentStep(step);
        globalThis.history.replaceState(
          null,
          '',
          `${globalThis.location.pathname}#${step}`,
        );
        if (entity) {
          updateTutorialProgress(userSettingsApi, entity.metadata.name, {
            totalSteps: total,
          });
        }
      }
    };

    // Wait for web-component upgrade
    const t = setTimeout(parseSteps, 400);

    const onHashChange = () => {
      const hash = globalThis.location.hash;
      const idx = parseInt(hash.replace('#', ''), 10);
      setCurrentStep(Number.isFinite(idx) ? idx : 0);
      // Persist progress on every step change via hash
      if (entity) {
        updateTutorialProgress(userSettingsApi, entity.metadata.name, {
          progress: hash || '#0',
        });
      }
    };
    globalThis.addEventListener('hashchange', onHashChange);

    return () => {
      clearTimeout(t);
      globalThis.removeEventListener('hashchange', onHashChange);
    };
  }, [codelabHtml, entity, userSettingsApi]);

  const setHash = useCallback((index: number) => {
    globalThis.location.hash = String(index);
  }, []);

  const handleStepClick = useCallback(
    (index: number) => {
      setHash(index);
    },
    [setHash],
  );

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      setHash(currentStep - 1);
    }
  }, [currentStep, setHash]);

  const handleNext = useCallback(() => {
    if (currentStep >= steps.length - 1) {
      if (entity) {
        updateTutorialProgress(userSettingsApi, entity.metadata.name, {
          progress: 'done',
        });
      }
      if (onDone) {
        onDone();
      } else {
        const done = document.querySelector<HTMLElement>('#done');
        if (done) done.click();
      }
    } else {
      setHash(currentStep + 1);
    }
  }, [currentStep, steps.length, onDone, entity, userSettingsApi, setHash]);

  useEffect(() => {
    if (onNavReady && steps.length > 0) {
      onNavReady({
        steps,
        currentStep,
        onStepClick: handleStepClick,
        onPrev: handlePrev,
        onNext: handleNext,
        onDone,
      });
    }
  }, [
    steps,
    currentStep,
    onNavReady,
    handleStepClick,
    handlePrev,
    handleNext,
    onDone,
  ]);

  useEffect(() => {
    if (!entity || !imgUrl || !indexUrl || !academyBaseUrl) {
      return () => {};
    }

    setLoadingAcademy(true);

    const scriptUrls = scripts.map(
      name => `${academyBaseUrl}/content/static/academy_src/${name}`,
    );

    loadScripts(scriptUrls, setError)
      .then(() => {
        // eslint-disable-next-line no-restricted-globals
        return Promise.all([customElements.whenDefined('google-codelab')]);
      })
      .then(() => {
        loadStylesheets(stylesheetUrls);
        renderCodelab(indexUrl)
          .then(htmlString => {
            if (htmlString) {
              const finalHtmlString = htmlString.replace(/img\//g, imgUrl);
              setCodelabHtml(finalHtmlString);
            }
          })
          .catch(err => {
            // eslint-disable-next-line no-console
            console.error('Error rendering codelab:', err);
          })
          .finally(() => {
            setLoadingAcademy(false);
          });
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error(
          'Error loading scripts or defining custom elements:',
          err,
        );
        setLoadingAcademy(false);
      });

    return () => {
      stylesheetUrls.forEach(stylesheetUrl => {
        const linkElement = document.querySelector(
          `link[href="${stylesheetUrl}"]`,
        );
        if (linkElement) {
          document.head.removeChild(linkElement);
        }
      });
    };
  }, [entity, imgUrl, indexUrl, academyBaseUrl]);

  const handleClose = () => {
    setIsNotificationOpen(false);
  };

  const codelabContent = (
    <>
      {loadingAcademy && <LinearProgress />}
      {entity && !externalNav && <CodelabOverlays entity={entity} />}
      {!loading && academyError && <ErrorPage error={academyError} />}
      {!loading && !academyError && error && <ErrorPage error={error} />}
      {!loading && !academyError && !error && (
        <>
          <CustomSnackbar
            open={!progress && isNotificationOpen}
            message={PROGRESS_MESSAGES.SYNCING}
            onClose={handleClose}
          />
          <CustomSnackbar
            open={progress === 'continue' && isNotificationOpen}
            message={PROGRESS_MESSAGES.SYNCED}
            onClose={handleClose}
          />
          <CustomSnackbar
            open={progress === 'restart' && isNotificationOpen}
            message={PROGRESS_MESSAGES.COMPLETED}
            onClose={handleClose}
          />
          {codelabHtml && (
            <>
              {!externalNav && (
                <div
                  ref={wrapperRef}
                  className={classes.codelabsLegacy}
                  dangerouslySetInnerHTML={{ __html: codelabHtml as any }}
                />
              )}
              {externalNav && (
                <div
                  ref={wrapperRef}
                  className={classes.codelabs}
                  dangerouslySetInnerHTML={{ __html: codelabHtml as any }}
                />
              )}
            </>
          )}
        </>
      )}
    </>
  );

  if (externalNav) {
    return codelabContent;
  }

  return (
    <Page themeId="tool">
      {showHeader && (
        <Header
          title="Academy"
          subtitle="Let's Learn Something New: Unlock Your Full Potential"
        >
          <HeaderLabel label="Owner" value="TechDocs" />
          <HeaderLabel label="Lifecycle" value="Alpha" />
        </Header>
      )}
      <Content noPadding={!showHeader}>
        <Container disableGutters maxWidth={false} sx={{ height: '100%' }}>
          {codelabContent}
        </Container>
      </Content>
    </Page>
  );
}
